/**
 * 登录人在某一个工作空间里的活跃成员身份。
 * 子账号 id 只有契约返回后才从成员对象读出，这里不生成。
 *
 * Callers: AccessSession、登录落地、导航权限缓存
 */

import type {
  WorkspacePermission,
  WorkspaceRoleSummary,
} from "@metaadsmanager/tk-master-contracts";

const activeWorkspaceChanged = "tk-master:active-workspace-changed";

export type WorkspaceActorMembership = {
  permissions: readonly WorkspacePermission[];
  role: WorkspaceRoleSummary;
  workspace: { id: string };
};

export type WorkspaceActor = {
  permissions: readonly WorkspacePermission[];
  role: WorkspaceRoleSummary;
  userId: string;
  workspaceId: string;
};

export type ActiveMembershipSelection<T> = {
  membership: T | null;
  resolvedWorkspaceId: string | null;
  /** 本地记录缺失、过期，或已经没有成员身份时需要改写。 */
  shouldPersist: boolean;
};

export function activeWorkspaceStorageKey(userId: string): string {
  return `tk-master.active-workspace.${userId}`;
}

export function readStoredActiveWorkspaceId(
  userId: string | undefined,
): string | null {
  if (typeof window === "undefined" || !userId) return null;
  try {
    const value = window.localStorage.getItem(activeWorkspaceStorageKey(userId));
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

export function writeStoredActiveWorkspaceId(
  userId: string,
  workspaceId: string,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      activeWorkspaceStorageKey(userId),
      workspaceId,
    );
  } catch {
    // 写失败时下一次仍按成员列表第一条选择。
  }
}

export function clearStoredActiveWorkspaceId(userId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(activeWorkspaceStorageKey(userId));
  } catch {
    // 清除失败时选择逻辑仍会忽略不在列表中的 id。
  }
}

export function notifyActiveWorkspaceChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(activeWorkspaceChanged));
}

export function subscribeActiveWorkspace(onStoreChange: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handleChange = () => onStoreChange();
  window.addEventListener(activeWorkspaceChanged, handleChange);
  window.addEventListener("storage", handleChange);
  return () => {
    window.removeEventListener(activeWorkspaceChanged, handleChange);
    window.removeEventListener("storage", handleChange);
  };
}

export function selectActiveMembership<
  T extends { workspace: { id: string } },
>(
  memberships: readonly T[],
  preferredWorkspaceId: string | null | undefined,
): ActiveMembershipSelection<T> {
  if (memberships.length === 0) {
    return {
      membership: null,
      resolvedWorkspaceId: null,
      shouldPersist: Boolean(preferredWorkspaceId),
    };
  }

  const match = preferredWorkspaceId
    ? memberships.find((item) => item.workspace.id === preferredWorkspaceId)
    : undefined;
  if (match) {
    return {
      membership: match,
      resolvedWorkspaceId: match.workspace.id,
      shouldPersist: false,
    };
  }

  const first = memberships[0]!;
  return {
    membership: first,
    resolvedWorkspaceId: first.workspace.id,
    shouldPersist: true,
  };
}

/** 把活跃成员收成调用方使用的身份字段。 */
export function toWorkspaceActor(
  userId: string,
  membership: WorkspaceActorMembership,
): WorkspaceActor {
  return {
    permissions: membership.permissions,
    role: membership.role,
    userId,
    workspaceId: membership.workspace.id,
  };
}
